// src/pages/ProfileManagement.jsx - FIXED VERSION with Dynamic Typography
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getSalonConfig } from '../api/salonConfigService';
import {
  requestNotificationPermission,
  saveCustomerFCMToken,
  isNotificationSupported,
  getNotificationPermission
} from '../services/fcmService';
import '../styles/profile.css';

// Helper function to convert font size to CSS value
const getFontSizeValue = (fontSize) => {
  switch (fontSize) {
    case 'small':
      return '14px';
    case 'large':
      return '18px';
    case 'medium':
    default:
      return '16px';
  }
};

function ProfileManagement() {
  const { salonId } = useParams();
  const location    = useLocation();
  const { currentUser, currentCustomer } = useCustomerAuth();
  const [loading, setLoading] = useState(true);

  // Section refs for sub-tab navigation from CustomerNavigation
  const personalInfoRef  = useRef(null);
  const servicePrefsRef  = useRef(null);
  const commPrefsRef     = useRef(null);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [browserPermission, setBrowserPermission] = useState(() => getNotificationPermission());
  const [fcmTokenRegistered, setFcmTokenRegistered] = useState(null); // null=unknown, true=ok, false=failed
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    postalCode: '',
    birthMonth: '',
    marketingPreferences: {
      email: false,
      pushNotifications: false,
      whatsapp: false,
      socialMedia: false
    },
    servicePreferences: {
      preferredDay: '',
      preferredTimes: [],
      stylist1: '',
      service1: '',
      stylist2: '',
      service2: ''
    }
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Days of the week options
  const daysOfWeek = [
    'No Preference',
    'Monday',
    'Tuesday', 
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  // Time slots from 9:00 AM to 6:00 PM
  const timeSlots = [
    'No Preference',
    '9:00 AM',
    '10:00 AM',
    '11:00 AM', 
    '12:00 PM',
    '1:00 PM',
    '2:00 PM',
    '3:00 PM',
    '4:00 PM',
    '5:00 PM',
    '6:00 PM'
  ];

  // Scroll to section when navigated from CustomerNavigation sub-tabs
  useEffect(() => {
    const section = location.state?.section;
    if (!section || loading) return;
    const refMap = {
      'personal-info':  personalInfoRef,
      'service-prefs':  servicePrefsRef,
      'comm-prefs':     commPrefsRef,
    };
    const target = refMap[section];
    if (target?.current) {
      setTimeout(() => {
        target.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [location.state?.section, loading]);

  // Load dynamic colors and typography from salon configuration
  useEffect(() => {
    const updateDynamicStyling = async () => {
      try {
        const salonConfig = await getSalonConfig(salonId);
        if (salonConfig?.branding) {
          const root = document.documentElement;
          
          // Apply colors
          if (salonConfig.branding.colors) {
            root.style.setProperty('--salon-primary', salonConfig.branding.colors.primary || '#E64C31');
            root.style.setProperty('--salon-secondary', salonConfig.branding.colors.secondary || '#FFBDB1');
            root.style.setProperty('--salon-accent', salonConfig.branding.colors.accent || '#F6F6F6');
            root.style.setProperty('--salon-background', salonConfig.branding.colors.background || '#FFFFFF');
            root.style.setProperty('--salon-text', salonConfig.branding.colors.text || '#333333');
          }
          
          // Apply typography
          if (salonConfig.branding.typography) {
            root.style.setProperty('--salon-heading-font', salonConfig.branding.typography.headingFont || 'Familjen Grotesk, sans-serif');
            root.style.setProperty('--salon-body-font', salonConfig.branding.typography.bodyFont || 'Wix Madefor Text, sans-serif');
            root.style.setProperty('--salon-font-size', getFontSizeValue(salonConfig.branding.typography.fontSize));
          }
        }
      } catch (error) {
        console.error('Error loading salon styling:', error);
      }
    };
    
    if (salonId) {
      updateDynamicStyling();
    }
  }, [salonId]);

  // Fetch stylists and services from Firestore
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);

        // Fetch stylists for this salon
        const staffQuery = query(
          collection(db, 'staff'),
          where('salonId', '==', salonId)
        );
        const staffSnapshot = await getDocs(staffQuery);
        const stylistsList = [];
        staffSnapshot.forEach((doc) => {
          stylistsList.push({
            id: doc.id,
            ...doc.data()
          });
        });

        // Fetch services for this salon
        const servicesQuery = query(
          collection(db, 'services'),
          where('salonId', '==', salonId)
        );
        const servicesSnapshot = await getDocs(servicesQuery);
        const servicesList = [];
        servicesSnapshot.forEach((doc) => {
          servicesList.push({
            id: doc.id,
            ...doc.data()
          });
        });

        setStylists(stylistsList);
        setServices(servicesList);
      } catch (error) {
        console.error('Error fetching stylists and services:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    if (salonId) {
      fetchOptions();
    }
  }, [salonId]);

  // Fetch customer profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        
        if (!currentCustomer) {
          throw new Error('User not authenticated');
        }
        
        const userEmail = currentCustomer.email;
        
        // Get customer profile
        const customerDocRef = doc(db, 'customers', `${userEmail}_${salonId}`);
        const customerDoc = await getDoc(customerDocRef);
        
        if (!customerDoc.exists()) {
          throw new Error('Customer profile not found');
        }
        
        const profileData = customerDoc.data();
        setProfile(profileData);
        
        // Split displayName into firstName and lastName
        let firstName = '';
        let lastName = '';
        
        if (profileData.displayName) {
          const nameParts = profileData.displayName.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }
        
        // Initialize form data
        // emailSubscribed reflects the canonical opt-in state: true unless
        // explicitly opted out via either preferences or unsubscribe link.
        const emailOptIn = profileData.preferences?.notifications?.email;
        const emailSub = profileData.subscriptionPreferences?.email;
        const emailEnabled = emailSub === false ? false : (emailOptIn !== false);
        setFormData({
          firstName: firstName,
          lastName: lastName,
          phone: profileData.phoneNumber || '',
          postalCode: profileData.postalCode || '',
          birthMonth: profileData.birthMonth || '',
          marketingPreferences: {
            email: emailEnabled,
            pushNotifications: profileData.preferences?.notifications?.push || false,
            whatsapp: profileData.preferences?.notifications?.whatsapp || false,
            socialMedia: profileData.preferences?.notifications?.socialMedia || false
          },
          servicePreferences: {
            preferredDay: profileData.servicePreferences?.preferredDay || '',
            preferredTimes: profileData.servicePreferences?.preferredTimes || [],
            stylist1: profileData.servicePreferences?.stylist1 || '',
            service1: profileData.servicePreferences?.service1 || '',
            stylist2: profileData.servicePreferences?.stylist2 || '',
            service2: profileData.servicePreferences?.service2 || ''
          }
        });
        
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [salonId, currentUser, currentCustomer]);

  // Convert stored stylist/service names back to IDs for dropdown selection
  useEffect(() => {
    if (profile?.servicePreferences && stylists.length > 0 && services.length > 0) {
      const prefs = profile.servicePreferences;
      const updatedPrefs = {
        preferredDay: prefs.preferredDay || '',
        preferredTimes: prefs.preferredTimes || [],
        stylist1: '',
        service1: '',
        stylist2: '',
        service2: ''
      };

      // Convert stylist1 name to ID
      if (prefs.stylist1) {
        const stylist = stylists.find(s =>
          (s.name === prefs.stylist1) || (s.displayName === prefs.stylist1)
        );
        updatedPrefs.stylist1 = stylist ? stylist.id : '';
      }

      // Convert service1 name to ID
      if (prefs.service1) {
        const service = services.find(s =>
          (s.name === prefs.service1) || (s.serviceName === prefs.service1)
        );
        updatedPrefs.service1 = service ? service.id : '';
      }

      // Convert stylist2 name to ID
      if (prefs.stylist2) {
        const stylist = stylists.find(s =>
          (s.name === prefs.stylist2) || (s.displayName === prefs.stylist2)
        );
        updatedPrefs.stylist2 = stylist ? stylist.id : '';
      }

      // Convert service2 name to ID
      if (prefs.service2) {
        const service = services.find(s =>
          (s.name === prefs.service2) || (s.serviceName === prefs.service2)
        );
        updatedPrefs.service2 = service ? service.id : '';
      }

      // Update form data with converted IDs
      setFormData(prevData => ({
        ...prevData,
        servicePreferences: updatedPrefs
      }));
    }
  }, [profile, stylists, services]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleMarketingPreferenceChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      marketingPreferences: {
        ...prevData.marketingPreferences,
        [name]: checked
      }
    }));
  };

  const handleServicePreferenceChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      servicePreferences: {
        ...prevData.servicePreferences,
        [name]: value
      }
    }));
  };

  const handleTimePreferenceChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prevData => {
      let newTimes = [...prevData.servicePreferences.preferredTimes];
      
      if (value === 'No Preference') {
        // If "No Preference" is selected, clear all other selections
        newTimes = checked ? ['No Preference'] : [];
      } else {
        // Remove "No Preference" if a specific time is selected
        newTimes = newTimes.filter(time => time !== 'No Preference');
        
        if (checked) {
          newTimes.push(value);
        } else {
          newTimes = newTimes.filter(time => time !== value);
        }
      }
      
      return {
        ...prevData,
        servicePreferences: {
          ...prevData.servicePreferences,
          preferredTimes: newTimes
        }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      if (!currentCustomer || !currentCustomer.email) {
        throw new Error('User not authenticated');
      }
      
      // Construct the customer document ID
      const customerId = `${currentCustomer.email.toLowerCase()}_${salonId}`;
      
      // Combine firstName and lastName back into displayName
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();

      // Convert stylist and service IDs to names for storage
      const servicePreferencesWithNames = {
        preferredDay: formData.servicePreferences.preferredDay,
        preferredTimes: formData.servicePreferences.preferredTimes,
        stylist1: '',
        service1: '',
        stylist2: '',
        service2: ''
      };

      // Convert stylist1 ID to name
      if (formData.servicePreferences.stylist1) {
        const stylist = stylists.find(s => s.id === formData.servicePreferences.stylist1);
        servicePreferencesWithNames.stylist1 = stylist ? (stylist.name || stylist.displayName || '') : '';
      }

      // Convert service1 ID to name
      if (formData.servicePreferences.service1) {
        const service = services.find(s => s.id === formData.servicePreferences.service1);
        servicePreferencesWithNames.service1 = service ? (service.name || service.serviceName || '') : '';
      }

      // Convert stylist2 ID to name
      if (formData.servicePreferences.stylist2) {
        const stylist = stylists.find(s => s.id === formData.servicePreferences.stylist2);
        servicePreferencesWithNames.stylist2 = stylist ? (stylist.name || stylist.displayName || '') : '';
      }

      // Convert service2 ID to name
      if (formData.servicePreferences.service2) {
        const service = services.find(s => s.id === formData.servicePreferences.service2);
        servicePreferencesWithNames.service2 = service ? (service.name || service.serviceName || '') : '';
      }

      // Prepare update data
      const updateData = {
        displayName: displayName,
        phoneNumber: formData.phone,
        postalCode: formData.postalCode,
        'preferences.notifications.email': formData.marketingPreferences.email,
        'preferences.notifications.push': formData.marketingPreferences.pushNotifications,
        'preferences.notifications.whatsapp': formData.marketingPreferences.whatsapp,
        'preferences.notifications.socialMedia': formData.marketingPreferences.socialMedia,
        // Keep subscriptionPreferences.email in sync so the unsubscribe/re-subscribe
        // state is reflected correctly in both the portal and campaign exclusion logic.
        'subscriptionPreferences.email': formData.marketingPreferences.email,
        servicePreferences: servicePreferencesWithNames,
        updatedAt: new Date()
      };
      
      // Only update birthMonth if it's not already set in the profile
      if (!profile?.birthMonth && formData.birthMonth) {
        updateData.birthMonth = formData.birthMonth;
      }
      
      // Update customer profile
      const customerDocRef = doc(db, 'customers', customerId);
      await updateDoc(customerDocRef, updateData);

      // If push notifications enabled, request browser permission and save FCM token.
      // This MUST happen inside a user-gesture handler (form submit) so the browser
      // shows the "Allow notifications?" prompt (first time) or silently refreshes
      // the token (if permission was already granted).
      if (formData.marketingPreferences.pushNotifications && isNotificationSupported()) {
        const token = await requestNotificationPermission();
        const newPermission = getNotificationPermission();
        setBrowserPermission(newPermission);
        if (token) {
          await saveCustomerFCMToken(customerId, token);
          setFcmTokenRegistered(true);
        } else {
          setFcmTokenRegistered(false);
        }
      }

      // Update local profile state
      setProfile(prevProfile => ({
        ...prevProfile,
        displayName: displayName,
        phoneNumber: formData.phone,
        postalCode: formData.postalCode,
        birthMonth: formData.birthMonth || prevProfile.birthMonth,
        preferences: {
          ...prevProfile.preferences,
          notifications: {
            ...prevProfile.preferences?.notifications,
            email: formData.marketingPreferences.email,
            sms: formData.marketingPreferences.pushNotifications,
            whatsapp: formData.marketingPreferences.whatsapp,
            socialMedia: formData.marketingPreferences.socialMedia
          }
        },
        servicePreferences: servicePreferencesWithNames,
        updatedAt: new Date()
      }));
      
      setSuccessMessage('Profile updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || loadingOptions) {
    return <div className="profile-loading">Loading your profile...</div>;
  }
  
  if (error && !profile) {
    return <div className="profile-error">Error: {error}</div>;
  }

  return (
    <div className="profile-management">
      <header className="profile-header">
        <h1>Manage Your Profile</h1>
        {profile?.salonName && <p className="salon-name">{profile.salonName}</p>}
      </header>
      
      <div className="profile-content">
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            {profile?.photoURL ? (
              <img src={profile.photoURL} alt={profile.displayName || 'User'} />
            ) : (
              <div className="avatar-placeholder">
                {formData.firstName?.charAt(0)}{formData.lastName?.charAt(0)}
              </div>
            )}
          </div>
          <h2>{profile?.displayName || `${formData.firstName} ${formData.lastName}`}</h2>
          <p className="member-since">
            Member since {profile?.createdAt && new Date(profile.createdAt.seconds * 1000).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
          <div className={`loyalty-badge ${(profile?.loyaltyTier || 'Bronze').toLowerCase()}`}>
            <span>
              {profile?.loyaltyTier || 'Bronze'} Member
            </span>
            <span className="points-display">
              {profile?.metrics?.pointsBalance || 0} Points
            </span>
          </div>
        </div>
        
        <form className="profile-form" onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}
          {successMessage && <div className="form-success">{successMessage}</div>}
          
          <div className="form-section" ref={personalInfoRef} id="section-personal-info">
            <h3>Personal Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(123) 456-7890"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="postalCode">Postal Code</label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="12345"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={currentCustomer?.email || ''}
                disabled
                style={{
                  backgroundColor: '#f5f5f5',
                  color: '#666',
                  cursor: 'not-allowed'
                }}
              />
              <div className="form-helper-text">
                Email address cannot be changed. Contact support if you need to update this.
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="birthMonth">Birth Month (for birthday offers)</label>
              {profile?.birthMonth ? (
                <div className="readonly-field">
                  <input
                    type="text"
                    value={profile.birthMonth}
                    disabled
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed'
                    }}
                  />
                  <div className="form-helper-text">
                    Birthday month cannot be changed once set for security reasons.
                  </div>
                </div>
              ) : (
                <>
                  <select
                    id="birthMonth"
                    name="birthMonth"
                    value={formData.birthMonth}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Month</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>
                  <div className="form-helper-text">
                    We'll send you a special offer during your birthday month! Note: This cannot be changed once set.
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="form-section" ref={servicePrefsRef} id="section-service-prefs">
            <h3>Services Preference</h3>
            
            <div className="form-group">
              <label htmlFor="preferredDay">Preferred Day of Booking</label>
              <select
                id="preferredDay"
                name="preferredDay"
                value={formData.servicePreferences.preferredDay}
                onChange={handleServicePreferenceChange}
              >
                {daysOfWeek.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Preferred Time (you can select multiple)</label>
              <div className="time-checkboxes">
                {timeSlots.map(time => (
                  <div key={time} className="checkbox-row">
                    <input
                      type="checkbox"
                      id={`time-${time}`}
                      value={time}
                      checked={formData.servicePreferences.preferredTimes.includes(time)}
                      onChange={handleTimePreferenceChange}
                    />
                    <label htmlFor={`time-${time}`} className="checkbox-label">
                      {time}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="preference-row">
              <div className="form-group">
                <label htmlFor="stylist1">Preferred Staff 1</label>
                <select
                  id="stylist1"
                  name="stylist1"
                  value={formData.servicePreferences.stylist1}
                  onChange={handleServicePreferenceChange}
                >
                  <option value="">Select Staff</option>
                  {stylists.map(stylist => (
                    <option key={stylist.id} value={stylist.id}>
                      {stylist.name || stylist.displayName}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="service1">Preferred Service 1</label>
                <select
                  id="service1"
                  name="service1"
                  value={formData.servicePreferences.service1}
                  onChange={handleServicePreferenceChange}
                >
                  <option value="">Select Service</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name || service.serviceName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="preference-row">
              <div className="form-group">
                <label htmlFor="stylist2">Preferred Staff 2</label>
                <select
                  id="stylist2"
                  name="stylist2"
                  value={formData.servicePreferences.stylist2}
                  onChange={handleServicePreferenceChange}
                >
                  <option value="">Select Staff</option>
                  {stylists.map(stylist => (
                    <option key={stylist.id} value={stylist.id}>
                      {stylist.name || stylist.displayName}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="service2">Preferred Service 2</label>
                <select
                  id="service2"
                  name="service2"
                  value={formData.servicePreferences.service2}
                  onChange={handleServicePreferenceChange}
                >
                  <option value="">Select Service</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name || service.serviceName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="form-section" ref={commPrefsRef} id="section-comm-prefs">
            <h3>Communication Preferences</h3>
            
            <div className="form-group">
              <h4>Email Notifications</h4>
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="emailMarketing"
                  name="email"
                  checked={formData.marketingPreferences.email}
                  onChange={handleMarketingPreferenceChange}
                />
                <label htmlFor="emailMarketing" className="checkbox-label">
                  Receive promotions, exclusive offers, and booking confirmations via email
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <h4>Push Notifications</h4>
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="pushNotifications"
                  name="pushNotifications"
                  checked={formData.marketingPreferences.pushNotifications}
                  onChange={handleMarketingPreferenceChange}
                />
                <label htmlFor="pushNotifications" className="checkbox-label">
                  Receive appointment reminders and updates via push notifications
                </label>
              </div>
              {formData.marketingPreferences.pushNotifications && isNotificationSupported() && (
                <div style={{ marginTop: '6px', fontSize: '13px' }}>
                  {browserPermission === 'granted' && fcmTokenRegistered === null && (
                    <span style={{ color: '#2e7d32' }}>
                      Browser notifications are already allowed. Click Save — no prompt will appear, your device will be registered silently.
                    </span>
                  )}
                  {browserPermission === 'granted' && fcmTokenRegistered === true && (
                    <span style={{ color: '#2e7d32' }}>
                      Your device is registered to receive push notifications.
                    </span>
                  )}
                  {browserPermission === 'granted' && fcmTokenRegistered === false && (
                    <span style={{ color: '#c62828' }}>
                      Browser permission is granted but token registration failed. Check the browser console for errors, then try saving again.
                    </span>
                  )}
                  {browserPermission === 'denied' && (
                    <span style={{ color: '#c62828' }}>
                      Browser notifications are blocked. To fix: click the lock/info icon in your browser address bar, set Notifications to "Allow", then save this page again.
                    </span>
                  )}
                  {browserPermission === 'default' && (
                    <span style={{ color: '#e65100' }}>
                      Click Save — your browser will ask for notification permission.
                    </span>
                  )}
                </div>
              )}
              {!isNotificationSupported() && formData.marketingPreferences.pushNotifications && (
                <div style={{ marginTop: '6px', fontSize: '13px', color: '#777' }}>
                  Push notifications are not supported in this browser. Try Chrome or Edge.
                </div>
              )}
            </div>

            <div className="form-group">
              <h4>WhatsApp Campaigns</h4>
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="whatsappMarketing"
                  name="whatsapp"
                  checked={formData.marketingPreferences.whatsapp}
                  onChange={handleMarketingPreferenceChange}
                />
                <label htmlFor="whatsappMarketing" className="checkbox-label">
                  Receive promotions, exclusive offers, and updates via WhatsApp
                </label>
              </div>
            </div>

            <div className="form-group">
              <h4>Social Media Campaigns (Meta, Instagram &amp; TikTok)</h4>
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="socialMediaMarketing"
                  name="socialMedia"
                  checked={formData.marketingPreferences.socialMedia}
                  onChange={handleMarketingPreferenceChange}
                />
                <label htmlFor="socialMediaMarketing" className="checkbox-label">
                  Include me in social media campaign audiences on Meta, Instagram and TikTok so I receive relevant promotions and offers
                </label>
              </div>
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="save-button"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileManagement;